import { Check, Minus } from 'lucide-react';
import type { ReactNode } from 'react';
import { EMPLOYMENT_TYPE_LABELS, WORK_MODE_LABELS, type RoleTemplateView } from '@nest-recruit/shared';
import { Chip } from '../../../../components/Chip';
import { useModule } from '../../../../lib/modules';
import { useDepartments, useVentures } from '../../../organisation/api/organisation-api';
import { shownStagesOf } from '../StageBuilder';
import { filledLines, offeredText, templatePlaceOf, usedText, type TemplateDraft } from './role-template-draft';
import styles from './AdminSetup.module.css';

/**
 * Role templates A, the right column: what a recruiter gets from this template, as it stands in the editor now — how it
 * looks in "Start from template", what it fills in for them, and the jobs already made from it, which keep their own
 * copy (non-negotiable #4).
 */
export function RecruiterView({ draft, template }: { readonly draft: TemplateDraft; readonly template: RoleTemplateView | null }): ReactNode {
  const ventures = useVentures();
  const departments = useDepartments();
  const isCriteriaOn = useModule('screening_criteria');
  const isFormOn = useModule('career_site');
  const stages = shownStagesOf(draft.pipeline.stages, { isAssessmentsOn: useModule('assessments'), isOffersOn: useModule('offers') });
  const ventureName = (ventures.data ?? []).find((venture) => venture.id === draft.ventureId)?.name ?? null;
  const departmentName = (departments.data ?? []).find((department) => department.id === draft.departmentId)?.name ?? null;
  const lines = filledLines(draft, { isCriteriaOn, isFormOn, stageCount: stages.filter((stage) => stage.isActive).length });
  const used = template?.timesUsed ?? 0;

  return (
    <aside className={styles.recruiter} aria-label="What a recruiter gets">
      <h2 className={styles.recruiterTitle}>What a recruiter gets</h2>
      <p className={styles.recruiterLabel}>In “Start from template”</p>
      <div className={styles.preview}>
        <span className={styles.itemTop}>
          <span className={styles.itemName}>{draft.name.trim() || 'Untitled template'}</span>
          {template?.isCertified && <Chip tone="success">Certified</Chip>}
        </span>
        <span className={styles.itemMeta}>{templatePlaceOf({ roleFamily: draft.roleFamily.trim() || null, ventureName, departmentName })}</span>
        <span className={styles.itemMeta}>
          {EMPLOYMENT_TYPE_LABELS[draft.employmentType]} · {WORK_MODE_LABELS[draft.workMode]} · {usedText(used)}
        </span>
      </div>
      <p className={styles.note}>{offeredText(template, draft.isActive)}</p>

      <p className={styles.recruiterLabel}>Filled in for them</p>
      <ul className={styles.filled}>
        {lines.map((line) => (
          <li key={line.label} className={styles.filledLine} data-empty={!line.isFilled || undefined}>
            {line.isFilled ? <Check size={14} aria-hidden /> : <Minus size={14} aria-hidden />}
            <span>{line.label}</span>
            <span className={styles.filledDetail}>{line.detail}</span>
          </li>
        ))}
      </ul>

      {template && (
        <p className={styles.note}>
          Jobs already made from it: <strong>{used}</strong>. They keep their own copy.
        </p>
      )}
    </aside>
  );
}
